import React from 'react';
import { camelCase } from 'lodash';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { UserInfo } from '../common/model/dto/user-info';
import { Resource } from '../common/model/type/resource.enum';
import { RecordColumn } from '../common/model/ui/record-list.type';
import DateUtils from '../common/utils/services/date-utils';
import {
  getUserDisplayName,
  getUserStatusTone,
} from '../common/utils/services/user-list-utils';
import { UserInfoFields } from '../firebase/document-field.enums';
import OvPillLink from '../components/UI/atoms/OvPillLink';
import OvStatusChip from '../components/UI/atoms/OvStatusChip';
import OvUserRowActions from '../components/UI/molecules/OvUserRowActions';
import useAuthorized from './use-authorized';

const USER_DATE_FORMAT = 'MMM DD, YYYY, HH:mm';

const formatUserDate = (date?: string | Date | null) =>
  date ? DateUtils.formatDate(date as Date, USER_DATE_FORMAT) : '-';

export function useUserListColumns(
  getNavigationState: () => unknown
): RecordColumn<UserInfo>[] {
  const { t } = useTranslation();
  const applicationOperations = useAuthorized(Resource.Application);
  const userOperations = useAuthorized(Resource.User);
  const readAllowed = userOperations.read || applicationOperations.supervise;

  const columns: RecordColumn<UserInfo>[] = [
    {
      id: 'name',
      header: t('common.userFields.name'),
      sortKey: UserInfoFields.first_name,
      cardRole: 'heading',
      renderCell: (user) => (
        <>
          {getUserDisplayName(user)}
          {user.is_demo_account && (
            <DemoTag>
              <OvStatusChip label={t('userList.demo')} tone="caution" />
            </DemoTag>
          )}
        </>
      ),
    },
    {
      id: 'email',
      header: t('common.userFields.email'),
      sortKey: UserInfoFields.email,
      renderCell: (user) => user.email || '-',
    },
    {
      id: 'clinic',
      header: t('common.userFields.clinic'),
      renderCell: (user) =>
        user.patient_clinic_locations?.length
          ? user.patient_clinic_locations.map(({ id, clinic_location }) => (
              <ClinicName key={id ?? clinic_location.name}>
                {clinic_location.name}
              </ClinicName>
            ))
          : '-',
    },
    {
      id: 'latest-scan',
      header: t('common.userFields.latestScanDate'),
      sortKey: UserInfoFields.latest_scan_date,
      renderCell: (user) => formatUserDate(user.latest_scan_date),
    },
    {
      id: 'registration',
      header: t('common.userFields.regDate'),
      sortKey: UserInfoFields.registration_date,
      renderCell: (user) =>
        formatUserDate(user.registration_date as unknown as Date),
    },
    {
      id: 'status',
      header: t('common.status'),
      renderCell: (user) =>
        user.status ? (
          <OvStatusChip
            label={t(`common.userFields.status.${camelCase(user.status)}`)}
            tone={getUserStatusTone(user.status)}
          />
        ) : (
          '-'
        ),
    },
    {
      id: 'operation',
      header: t('common.operation'),
      isHeaderHidden: true,
      renderCell: (user) => <OvUserRowActions user={user} />,
    },
  ];

  if (!readAllowed) {
    return columns;
  }

  return [
    ...columns,
    {
      id: 'action',
      header: t('common.action'),
      isHeaderHidden: true,
      cardRole: 'footer',
      renderCell: (user, detailsPath = '') => (
        <OvPillLink
          to={detailsPath}
          state={getNavigationState()}
          aria-label={t('userList.detailsFor', {
            name: getUserDisplayName(user),
          })}
        >
          {t('common.actions.viewProfile')}
        </OvPillLink>
      ),
    },
  ];
}

const DemoTag = styled.span`
  display: block;
  margin-top: 0.25rem;
`;

const ClinicName = styled.span`
  display: block;
`;
